import {
  CalendarCheck,
  Calculator,
  CheckCircle2,
  FileSpreadsheet,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getTenantSlug } from "@/lib/tenant";
import { useAuthStore } from "@/stores/auth";

/**
 * Public landing page (root domain only).
 *
 *  - On a tenant subdomain → bounce to /dashboard if authed, else /login
 *  - On the root domain → marketing landing
 */
export default function HomePage() {
  const { t } = useTranslation();
  const tenantSlug = getTenantSlug();
  const isHydrated = useAuthStore((s) => s.isHydrated);
  const user = useAuthStore((s) => s.user);

  if (!isHydrated) return null;
  if (tenantSlug) {
    return <Navigate to={user ? "/dashboard" : "/login"} replace />;
  }

  return (
    <div className="min-h-screen bg-surface-page">
      <header className="border-b border-slate-200 bg-surface">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-bold text-slate-900">{t("app.title")}</span>
            <span className="text-xs text-slate-500">{t("app.tagline")}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm">
              <Link to="/login">{t("auth.login")}</Link>
            </Button>
            <Button asChild size="sm">
              <Link to="/signup">{t("auth.start_trial")}</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-16 space-y-20">
        {/* Hero */}
        <section className="text-center">
          <h1 className="m-0 text-4xl font-bold text-slate-900 md:text-5xl">
            {t("landing.hero_title")}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
            {t("landing.hero_subtitle")}
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg">
              <Link to="/signup">{t("auth.start_trial")}</Link>
            </Button>
            <Button asChild variant="secondary" size="lg">
              <Link to="/login">{t("auth.login")}</Link>
            </Button>
          </div>
          <p className="mt-4 text-xs text-slate-500">{t("home.subdomain_hint")}</p>
        </section>

        {/* How it works */}
        <section>
          <h2 className="text-center text-2xl font-bold text-slate-900">
            {t("landing.how_title")}
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-4">
            <FeatureCard
              icon={Users}
              title={t("landing.step_1_title")}
              body={t("landing.step_1_body")}
            />
            <FeatureCard
              icon={CalendarCheck}
              title={t("landing.step_2_title")}
              body={t("landing.step_2_body")}
            />
            <FeatureCard
              icon={Calculator}
              title={t("landing.step_3_title")}
              body={t("landing.step_3_body")}
            />
            <FeatureCard
              icon={FileSpreadsheet}
              title={t("landing.step_4_title")}
              body={t("landing.step_4_body")}
            />
          </div>
        </section>

        {/* Compliance trust strip */}
        <section className="rounded-lg border border-slate-200 bg-surface p-8">
          <div className="flex items-center justify-center gap-2 text-sm font-medium text-slate-600">
            <ShieldCheck className="h-5 w-5 text-success" aria-hidden="true" />
            {t("landing.compliance_strip")}
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-6 text-sm font-medium text-slate-700">
            <span>BIR</span>
            <span aria-hidden="true">·</span>
            <span>SSS</span>
            <span aria-hidden="true">·</span>
            <span>PhilHealth</span>
            <span aria-hidden="true">·</span>
            <span>Pag-IBIG</span>
            <span aria-hidden="true">·</span>
            <span>DOLE</span>
            <span aria-hidden="true">·</span>
            <span>RA 10173</span>
          </div>
        </section>

        {/* Pricing */}
        <section>
          <h2 className="text-center text-2xl font-bold text-slate-900">
            {t("landing.pricing_title")}
          </h2>
          <p className="mt-2 text-center text-sm text-slate-600">
            {t("landing.pricing_subtitle")}
          </p>
          <div className="mx-auto mt-8 max-w-md">
            <Card className="border-primary">
              <CardContent className="p-8 text-center">
                <p className="m-0 text-5xl font-bold text-primary">₱9,000</p>
                <p className="m-0 mt-1 text-sm text-slate-600">
                  {t("landing.pricing_per_month")}
                </p>
                <p className="m-0 mt-2 text-xs text-slate-500">
                  {t("landing.pricing_annual")}
                </p>
                <ul className="mx-auto mt-6 max-w-xs space-y-2 text-left text-sm text-slate-700">
                  <Bullet>{t("landing.feat_employees")}</Bullet>
                  <Bullet>{t("landing.feat_all_features")}</Bullet>
                  <Bullet>{t("landing.feat_filings")}</Bullet>
                  <Bullet>{t("landing.feat_ess")}</Bullet>
                  <Bullet>{t("landing.feat_trial")}</Bullet>
                </ul>
                <Button asChild size="lg" className="mt-6 w-full">
                  <Link to="/signup">{t("auth.start_trial")}</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200 bg-surface">
        <div className="mx-auto max-w-6xl px-6 py-6 text-center text-xs text-slate-500">
          KraetdTech Industries{" "}
          <span aria-hidden="true">·</span>{" "}
          <Link to="/privacy" className="hover:text-slate-700">
            {t("landing.footer_privacy")}
          </Link>{" "}
          <span aria-hidden="true">·</span>{" "}
          <Link to="/terms" className="hover:text-slate-700">
            {t("landing.footer_terms")}
          </Link>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  body,
}: {
  icon: typeof Users;
  title: string;
  body: string;
}) {
  return (
    <Card>
      <CardContent className="p-6">
        <Icon className="h-8 w-8 text-primary" aria-hidden="true" />
        <h3 className="m-0 mt-3 text-base font-semibold text-slate-900">{title}</h3>
        <p className="m-0 mt-2 text-sm text-slate-600">{body}</p>
      </CardContent>
    </Card>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />
      <span>{children}</span>
    </li>
  );
}
